import { createVuetify, type ThemeDefinition } from 'vuetify';
import { aliases, mdi } from 'vuetify/iconsets/mdi-svg';
import { createVueI18nAdapter } from 'vuetify/locale/adapters/vue-i18n';
import { useI18n } from 'vue-i18n';
import { i18n } from './i18n.ts';
import 'vuetify/styles';

type AdapterI18n = Parameters<typeof createVueI18nAdapter>[0]['i18n'];

/**
 * Vuetify 4, configured once.
 *
 * ICONS AS SVG PATHS. `mdi-svg` with `@mdi/js` imports each icon as a string
 * constant, so the bundle carries the forty icons this app uses rather than a
 * 400 KB icon font of seven thousand. It also removes a network request that
 * would otherwise block the first render of every icon.
 *
 * ONE LANGUAGE SOURCE. The vue-i18n adapter makes Vuetify read its own strings
 * (a data table's "Items per page", a dialog's "Close") from the same i18n
 * instance as the app. Without it the app switches to English and the table
 * footer stays German, which is the kind of half-translation that makes a
 * product feel unfinished.
 */

/**
 * Colours. A deep water blue for the brand, and status colours chosen for what
 * they mean in a control room rather than for decoration: `error` is reserved
 * for things an operator must act on (a leak, a silent device), and nothing
 * decorative is allowed to borrow it.
 */
const light: ThemeDefinition = {
  dark: false,
  colors: {
    background: '#f4f6f8',
    surface: '#ffffff',
    'surface-variant': '#e8edf2',
    primary: '#0f5c8c',
    secondary: '#3d6b7d',
    info: '#1769aa',
    success: '#2e7d32',
    warning: '#b45309',
    error: '#c62828',
  },
};

const dark: ThemeDefinition = {
  dark: true,
  colors: {
    background: '#0d141b',
    surface: '#141e27',
    'surface-variant': '#22303c',
    primary: '#5fb3e6',
    secondary: '#8fb5c4',
    info: '#64b5f6',
    success: '#66bb6a',
    warning: '#f0a04b',
    error: '#ef7070',
  },
};

export const vuetify = createVuetify({
  icons: { defaultSet: 'mdi', aliases, sets: { mdi } },
  // The adapter is typed for an i18n instance whose locale is any string. Ours
  // is narrowed to 'de' | 'en', which is stricter, not different, so the
  // widening here loses nothing the rest of the app relies on.
  locale: { adapter: createVueI18nAdapter({ i18n: i18n as unknown as AdapterI18n, useI18n }) },
  theme: {
    // 'system' follows the operating system until the operator picks one.
    // A control room at night and an office at noon want different answers.
    defaultTheme: 'system',
    themes: { light, dark },
  },
  defaults: {
    VCard: { variant: 'flat', rounded: 'lg' },
    VBtn: { variant: 'text' },
    VTextField: { variant: 'outlined', density: 'comfortable' },
    VSelect: { variant: 'outlined', density: 'comfortable' },
    VChip: { size: 'small', variant: 'tonal' },
  },
});
